export interface MatRecord {
  id: string;
  number: number;
  name: string;
  bracket_url: string | null;
  remote_muted: boolean;
  path: string;
}

export interface TournamentRecord {
  id: string;
  name: string;
  slug: string;
  starts_on: string | null;
  ends_on: string | null;
  status: 'active' | 'archived';
  created_at: string;
  mats: MatRecord[];
}

export type TournamentListState =
  | { kind: 'signed-out' }
  | { kind: 'error' }
  | { kind: 'ready'; tournaments: TournamentRecord[] };

const ERRORS: Record<string, string> = {
  invalid_name: 'Enter a name.',
  invalid_slug: 'Use lowercase letters, digits, and hyphens.',
  slug_taken: 'That slug is already used.',
  invalid_date: 'Use a calendar day, like 2026-03-01. A time is not a date.',
  date_order: 'The end date cannot be before the start date.',
  invalid_status: 'That status is not valid.',
  invalid_number: 'Mat numbers are 1 through 30.',
  mat_number_taken: 'That mat number is already used.',
  mat_cap: 'A tournament can have 30 mats.',
  empty: 'Nothing to change.',
  invalid_json: 'That request was not valid.',
  forbidden: 'Only an Admin can change the tournament.',
  origin: 'That request was rejected.',
  unauthorized: 'Sign in again.',
  not_found: 'That tournament or mat is no longer there.',
  unavailable: 'Mat Monitor could not reach its database.',
};

export async function loadTournaments(status: 'active' | 'archived'): Promise<TournamentListState> {
  try {
    const response = await fetch(`/api/tournaments?status=${status}`);
    if (response.status === 401) return { kind: 'signed-out' };
    if (!response.ok) return { kind: 'error' };
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null || !('tournaments' in body) || !Array.isArray(body.tournaments)) {
      return { kind: 'error' };
    }
    return { kind: 'ready', tournaments: body.tournaments.map((entry) => parseTournament(entry)) };
  } catch {
    return { kind: 'error' };
  }
}

export async function createTournament(input: {
  name: string;
  slug: string;
  starts_on: string | null;
  ends_on: string | null;
}): Promise<string | null> {
  const response = await fetch('/api/tournaments', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (response.ok) return null;
  return readError(response);
}

export async function saveTournament(
  id: string,
  input: { name: string; slug: string; starts_on: string | null; ends_on: string | null },
): Promise<string | null> {
  const response = await fetch(`/api/tournaments/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (response.ok) return null;
  return readError(response);
}

export async function setTournamentStatus(id: string, status: 'active' | 'archived'): Promise<string | null> {
  const response = await fetch(`/api/tournaments/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (response.ok) return null;
  return readError(response);
}

export async function addMat(tournamentId: string, input: { number: number; name: string }): Promise<string | null> {
  const response = await fetch(`/api/tournaments/${tournamentId}/mats`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (response.ok) return null;
  return readError(response);
}

export async function saveMat(
  tournamentId: string,
  matId: string,
  input: { name: string; bracket_url: string },
): Promise<string | null> {
  const response = await fetch(`/api/tournaments/${tournamentId}/mats/${matId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (response.ok) return null;
  return readError(response);
}

export async function removeMat(tournamentId: string, matId: string): Promise<string | null> {
  const response = await fetch(`/api/tournaments/${tournamentId}/mats/${matId}`, { method: 'DELETE' });
  if (response.ok) return null;
  return readError(response);
}

async function readError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string') {
      if (body.error === 'bracket_url' && 'message' in body && typeof body.message === 'string' && body.message) {
        return body.message;
      }
      return ERRORS[body.error] ?? 'That did not work.';
    }
  } catch {
    // The status is enough when the body is not JSON.
  }
  if (response.status === 403) return ERRORS.forbidden ?? 'Not allowed.';
  return 'That did not work.';
}

function parseTournament(value: unknown): TournamentRecord {
  if (typeof value !== 'object' || value === null) throw new Error('bad tournament');
  const record = value as Record<string, unknown>;
  if (record.status !== 'active' && record.status !== 'archived') throw new Error('bad status');
  if (typeof record.id !== 'string' || typeof record.name !== 'string' || typeof record.slug !== 'string') {
    throw new Error('bad tournament');
  }
  if (!Array.isArray(record.mats)) throw new Error('bad mats');
  return {
    id: record.id,
    name: record.name,
    slug: record.slug,
    starts_on: record.starts_on === null ? null : requireText(record.starts_on),
    ends_on: record.ends_on === null ? null : requireText(record.ends_on),
    status: record.status,
    created_at: requireText(record.created_at),
    mats: record.mats.map((entry) => parseMat(entry)),
  };
}

function parseMat(value: unknown): MatRecord {
  if (typeof value !== 'object' || value === null) throw new Error('bad mat');
  const record = value as Record<string, unknown>;
  if (typeof record.id !== 'string' || typeof record.name !== 'string' || typeof record.path !== 'string') {
    throw new Error('bad mat');
  }
  if (typeof record.number !== 'number') throw new Error('bad mat');
  return {
    id: record.id,
    number: record.number,
    name: record.name,
    bracket_url: record.bracket_url === null ? null : requireText(record.bracket_url),
    remote_muted: record.remote_muted === true,
    path: record.path,
  };
}

function requireText(value: unknown): string {
  if (typeof value !== 'string') throw new Error('bad text');
  return value;
}
